/**
 * Super-admin (saas_owner) API client. Mirrors the auth/vault clients but talks
 * to /api/v1/admin — endpoints are role-gated server-side, no workspace header.
 */

const API_BASE = "/api/v1";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
  meta?: { total: number; page: number; limit: number; totalPages: number };
}

export interface Page<T> {
  rows: T[];
  total: number;
  page: number;
  totalPages: number;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("auth_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function req<T>(url: string, options: RequestInit = {}): Promise<Envelope<T>> {
  const res = await fetch(`${API_BASE}${url}`, { ...options, headers: { ...authHeaders(), ...(options.headers as Record<string, string>) } });
  const json = (await res.json()) as Envelope<T>;
  if (!res.ok || !json.success) throw new Error(json.message || "Request failed");
  return json;
}

const get = <T>(url: string) => req<T>(url).then((j) => j.data);
const page = async <T>(url: string): Promise<Page<T>> => {
  const j = await req<T[]>(url);
  return { rows: j.data, total: j.meta?.total ?? j.data.length, page: j.meta?.page ?? 1, totalPages: j.meta?.totalPages ?? 1 };
};

// ─────────────────────────── Types ──────────────────────────────────────────

export interface AdminStats {
  users: { total: number; pending: number; approved: number; banned: number };
  workspaces: number;
  models: number;
  embeds: { count: number; views: number };
  activeSubscriptions: number;
  mrr: number;
  planDistribution: { planId: string | null; name: string; count: number }[];
}
export interface AdminTimeseries {
  days: number;
  series: { date: string; signups: number; revenue: number }[];
}

export type AccountStatus = "pending" | "approved" | "banned";
export type UserRole = "saas_owner" | "saas_user";

export interface AdminUser {
  _id: string;
  name: string;
  email: string;
  role: UserRole;
  accountStatus: AccountStatus;
  companyName?: string;
  avatar?: string;
  lastLoginAt?: string;
  createdAt: string;
}

export interface AdminUserWorkspace {
  id: string;
  name: string;
  slug: string;
  isOwner: boolean;
  billingType: "stripe" | "manual";
  plan: { id: string; name: string; monthlyPrice: number } | null;
  planLapsed: boolean;
  manualPlanEndsAt: string | null;
  modelCount: number;
  memberCount: number;
  storageEnabled: boolean;
  createdAt: string;
}
export interface AdminUserDetail {
  user: AdminUser;
  workspaces: AdminUserWorkspace[];
}

export interface AdminWorkspaceRow {
  id: string;
  name: string;
  slug: string;
  owner: { name: string; email: string } | null;
  billingType: "stripe" | "manual";
  plan: { id: string; name: string; monthlyPrice: number } | null;
  planLapsed: boolean;
  manualPlanEndsAt: string | null;
  storageEnabled: boolean;
  modelCount: number;
  memberCount: number;
  createdAt: string;
}

export interface AdminPlan {
  _id: string;
  name: string;
  description: string;
  status: "active" | "draft" | "archived";
  monthlyPrice: number;
  annualPrice: number;
  features: string[];
  limits: { maxModels: number; maxConfigurators: number; maxDomains: number; embedViews: number };
  maxMembers: number;
  storageEnabled: boolean;
  popular: boolean;
  stripeProductId?: string;
  createdAt: string;
}
export type PlanInput = Partial<Omit<AdminPlan, "_id" | "createdAt" | "stripeProductId">>;

export interface AdminInvoiceLine { description: string; quantity: number; unitPrice: number; total: number }
export interface AdminInvoice {
  _id: string;
  number: string;
  workspaceId: string;
  workspace: { name: string; slug: string } | null;
  kind: "plan" | "top_up";
  status: "draft" | "open" | "paid" | "void" | "uncollectible";
  lineItems: AdminInvoiceLine[];
  subtotal: number;
  tax: number;
  total: number;
  currency: string;
  dueDate?: string;
  paidAt?: string;
  notes?: string;
  createdAt: string;
}
export interface StripeInvoice {
  id: string;
  number: string | null;
  status: string | null;
  total: number;
  currency: string;
  customerEmail: string | null;
  customerName: string | null;
  created: number | null;
  hostedUrl: string | null;
  pdfUrl: string | null;
}

export interface AuditEntry {
  _id: string;
  action: string;
  actor: { name: string; email: string } | null;
  target?: { type?: string; id?: string; label?: string };
  metadata?: Record<string, unknown>;
  ip?: string;
  createdAt: string;
}

export interface Broadcast {
  _id: string;
  title: string;
  message: string;
  level: "info" | "warn" | "success";
  actionUrl?: string;
  actionLabel?: string;
  active: boolean;
  expiresAt?: string;
  createdAt: string;
}

// ─────────────────────────── Endpoints ──────────────────────────────────────

export const getAdminStats = () => get<AdminStats>("/admin/stats");
export const getAdminTimeseries = (days = 30) => get<AdminTimeseries>(`/admin/stats/timeseries?days=${days}`);

export const listAdminUsers = (params: { page?: number; limit?: number; search?: string; accountStatus?: string; role?: string } = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v != null && v !== "" && q.set(k, String(v)));
  return page<AdminUser>(`/admin/users?${q.toString()}`);
};
export const getAdminUser = (id: string) => get<AdminUserDetail>(`/admin/users/${id}`);
export const patchAdminUser = (id: string, body: { name?: string; accountStatus?: AccountStatus; role?: UserRole }) =>
  req<AdminUser>(`/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) }).then((j) => j.data);
export const deleteAdminUser = (id: string) =>
  req<{ id: string }>(`/admin/users/${id}`, { method: "DELETE" }).then((j) => j.data);
export const impersonateUser = (id: string) =>
  req<{ token: string; user: { id: string; name: string; email: string } }>(`/admin/users/${id}/impersonate`, { method: "POST" }).then((j) => j.data);

export const listAdminWorkspaces = (params: { page?: number; limit?: number; search?: string } = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v != null && v !== "" && q.set(k, String(v)));
  return page<AdminWorkspaceRow>(`/admin/workspaces?${q.toString()}`);
};
export const deleteAdminWorkspace = (id: string) =>
  req<{ id: string }>(`/admin/workspaces/${id}`, { method: "DELETE" }).then((j) => j.data);
export const setWorkspaceBilling = (id: string, body: { billingType: "stripe" | "manual"; manualPlanId?: string | null; manualPlanEndsAt?: string | null }) =>
  req<{ id: string; billingType: string; manualPlanId: string | null; manualPlanEndsAt: string | null }>(`/admin/workspaces/${id}/billing`, { method: "POST", body: JSON.stringify(body) }).then((j) => j.data);

export const listAdminPlans = () => get<AdminPlan[]>("/admin/plans");
export const createAdminPlan = (body: PlanInput) => req<AdminPlan>("/admin/plans", { method: "POST", body: JSON.stringify(body) }).then((j) => j.data);
export const updateAdminPlan = (id: string, body: PlanInput) => req<AdminPlan>(`/admin/plans/${id}`, { method: "PATCH", body: JSON.stringify(body) }).then((j) => j.data);
export const deleteAdminPlan = (id: string) => req<AdminPlan>(`/admin/plans/${id}`, { method: "DELETE" }).then((j) => j.data);

export const listAdminInvoices = (params: { page?: number; limit?: number; workspaceId?: string; status?: string; kind?: string } = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v != null && v !== "" && q.set(k, String(v)));
  return page<AdminInvoice>(`/admin/invoices?${q.toString()}`);
};
export const listStripeInvoices = (limit = 50) => get<StripeInvoice[]>(`/admin/invoices/stripe?limit=${limit}`);
export const createAdminInvoice = (body: {
  workspaceId: string;
  kind: "plan" | "top_up";
  lineItems: { description: string; quantity: number; unitPrice: number }[];
  tax?: number;
  currency?: string;
  dueDate?: string;
  notes?: string;
}) => req<AdminInvoice>("/admin/invoices", { method: "POST", body: JSON.stringify(body) }).then((j) => j.data);
export const setInvoiceStatus = (id: string, status: AdminInvoice["status"]) =>
  req<AdminInvoice>(`/admin/invoices/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }).then((j) => j.data);

export const listAudit = (params: { page?: number; limit?: number; action?: string } = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v != null && v !== "" && q.set(k, String(v)));
  return page<AuditEntry>(`/admin/audit?${q.toString()}`);
};

export const listBroadcasts = () => get<Broadcast[]>("/admin/broadcasts");
export const listActiveBroadcasts = () => get<Broadcast[]>("/admin/broadcasts/active");
export const createBroadcast = (body: Partial<Broadcast>) => req<Broadcast>("/admin/broadcasts", { method: "POST", body: JSON.stringify(body) }).then((j) => j.data);
export const updateBroadcast = (id: string, body: Partial<Broadcast>) => req<Broadcast>(`/admin/broadcasts/${id}`, { method: "PATCH", body: JSON.stringify(body) }).then((j) => j.data);
export const deleteBroadcast = (id: string) => req<{ id: string }>(`/admin/broadcasts/${id}`, { method: "DELETE" }).then((j) => j.data);

// ───────────────────────── Preset library (system scope) ────────────────────
// The curated library every workspace sees. Authored here; read by the editor
// through src/ui/editor/library/useManifest.ts, which falls back to the static
// public/ manifests when the API cannot answer.

/** Mirrors PRESET_KINDS in server/modules/presets/preset.model.ts. Kept in sync
 *  by a check in scripts/check-server-logic.ts — the two are one vocabulary. */
export const PRESET_KINDS = [
  "metal", "gem", "env", "fx", "look",
  "background", "gemEnv", "ground", "stage", "scene", "pose", "lut", "onBody",
  "ceramic", "pearl", "other",
] as const;
export type PresetKind = (typeof PRESET_KINDS)[number];
export type PresetTier = "free" | "premium" | "business";
export type PresetStatus = "draft" | "published";

export interface SystemPreset {
  _id: string;
  scope: "system" | "workspace";
  kind: PresetKind;
  name: string;
  values: Record<string, unknown>;
  assetId: string | null;
  fileUrl: string | null;
  thumbUrl: string | null;
  order: number;
  status: PresetStatus;
  tags: string[];
  tier: PresetTier;
  createdAt: string;
  updatedAt: string;
}

/** VaultAsset kinds — the asset row's type, distinct from the preset's kind. */
export type AssetKind =
  | "doc" | "image" | "render" | "hdr" | "exr" | "glb" | "vjson" | "json" | "pmat" | "dmat" | "lut";

export interface SystemPresetInput {
  kind: PresetKind;
  name: string;
  values?: Record<string, unknown>;
  /** Type of the uploaded file. Omitting it stores the asset as 'doc'. */
  assetKind?: AssetKind;
  dataUrl?: string;
  thumbDataUrl?: string;
  tags?: string[];
  order?: number;
  status?: PresetStatus;
  tier?: PresetTier;
}

export const listSystemPresets = (kind?: PresetKind) =>
  get<SystemPreset[]>(`/admin/presets${kind ? `?kind=${encodeURIComponent(kind)}` : ""}`);

export const createSystemPreset = (body: SystemPresetInput) =>
  req<SystemPreset>("/admin/presets", { method: "POST", body: JSON.stringify(body) }).then((j) => j.data);

export const updateSystemPreset = (
  id: string,
  body: Partial<Pick<SystemPreset, "name" | "order" | "status" | "tier" | "tags" | "thumbUrl">>,
) => req<SystemPreset>(`/admin/presets/${id}`, { method: "PATCH", body: JSON.stringify(body) }).then((j) => j.data);

export const deleteSystemPreset = (id: string) =>
  req<{ id: string }>(`/admin/presets/${id}`, { method: "DELETE" }).then((j) => j.data);
